import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
/*
 * © 2022 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import { UploadContainer, ROOT_PREFIX } from './uploadStyles';
import { EmbersLoading } from '../loading/loading';
import { EmbersLink } from '../link/link';
import { InteractiveIcon } from '../interactiveIcon/interactiveIcon';
import { EmbersStatusIcon } from '../statusIcon';
var Upload = function Upload(_ref, ref) {
  var _classnames;
  var _ref$isFileValid = _ref.isFileValid,
    isFileValid = _ref$isFileValid === void 0 ? function () {
      return true;
    } : _ref$isFileValid,
    onChange = _ref.onChange,
    onTypeError = _ref.onTypeError,
    uploading = _ref.uploading,
    errorMessage = _ref.errorMessage,
    uploadSuccess = _ref.uploadSuccess,
    _ref$accept = _ref.accept,
    accept = _ref$accept === void 0 ? '' : _ref$accept,
    uploadDescription = _ref.uploadDescription,
    _ref$customFileName = _ref.customFileName,
    customFileName = _ref$customFileName === void 0 ? '' : _ref$customFileName,
    initDisplay = _ref.initDisplay,
    successDisplay = _ref.successDisplay,
    deleteLabel = _ref.deleteLabel,
    _ref$allowMultipleFil = _ref.allowMultipleFiles,
    allowMultipleFiles = _ref$allowMultipleFil === void 0 ? true : _ref$allowMultipleFil,
    onMultipleFilesError = _ref.onMultipleFilesError;
  var intl = useIntl();
  var inputRef = useRef(null);
  var _useState = useState('init'),
    _useState2 = _slicedToArray(_useState, 2),
    uploadState = _useState2[0],
    setUploadState = _useState2[1];
  var _useState3 = useState(customFileName),
    _useState4 = _slicedToArray(_useState3, 2),
    fileName = _useState4[0],
    setFileName = _useState4[1];
  var _useState5 = useState(false),
    _useState6 = _slicedToArray(_useState5, 2),
    active = _useState6[0],
    setActive = _useState6[1];
  useEffect(function () {
    setUploadState(uploading ? 'processing' : errorMessage ? 'error' : uploadSuccess ? 'success' : 'init');
  }, [uploadSuccess, uploading, errorMessage]);
  useEffect(function () {
    setFileName(customFileName);
  }, [customFileName]);
  useImperativeHandle(ref, function () {
    return {
      click: function click() {
        var _inputRef$current;
        inputRef === null || inputRef === void 0 || (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 || _inputRef$current.click();
      },
      focus: function focus() {
        var _inputRef$current2;
        inputRef === null || inputRef === void 0 || (_inputRef$current2 = inputRef.current) === null || _inputRef$current2 === void 0 || _inputRef$current2.focus();
      }
    };
  });
  var isValidType = function isValidType(file) {
    if (accept) {
      var extension = file.name.split('.').length > 1 ? file.name.split('.').pop() : '';
      var accepctArr = accept.split(',');
      if (file.type && (extension && accepctArr.indexOf('.' + extension) > -1 || accepctArr.indexOf(file.type) > -1 || file.type.includes('/') && accepctArr.indexOf(file.type.split('/')[0] + '/*') > -1)) {
        return true;
      } else {
        onTypeError === null || onTypeError === void 0 || onTypeError(file);
        return false;
      }
    }
    return true;
  };
  var checkFile = function checkFile(file) {
    return isValidType(file) && (isFileValid === null || isFileValid === void 0 ? void 0 : isFileValid(file));
  };
  var handleFileChange = function handleFileChange(file, uploadType) {
    onChange === null || onChange === void 0 || onChange(file, uploadType);
    setFileName(file.name);
    if (onChange && inputRef.current) {
      inputRef.current.value = '';
    }
  };
  var onClick = function onClick() {
    var _inputRef$current3;
    if (uploadState === 'init' || uploadState === 'error') inputRef === null || inputRef === void 0 || (_inputRef$current3 = inputRef.current) === null || _inputRef$current3 === void 0 || _inputRef$current3.click();
  };
  var _onChange = function _onChange(e) {
    var _e$currentTarget$file, _e$currentTarget$file2, _e$currentTarget$file3;
    ((_e$currentTarget$file = e.currentTarget.files) === null || _e$currentTarget$file === void 0 ? void 0 : _e$currentTarget$file[0]) && checkFile((_e$currentTarget$file2 = e.currentTarget.files) === null || _e$currentTarget$file2 === void 0 ? void 0 : _e$currentTarget$file2[0]) && handleFileChange((_e$currentTarget$file3 = e.currentTarget.files) === null || _e$currentTarget$file3 === void 0 ? void 0 : _e$currentTarget$file3[0], 'browse');
  };
  var onDrop = function onDrop(e) {
    var _e$dataTransfer;
    e.preventDefault();
    var fileLength = e === null || e === void 0 || (_e$dataTransfer = e.dataTransfer) === null || _e$dataTransfer === void 0 || (_e$dataTransfer = _e$dataTransfer.files) === null || _e$dataTransfer === void 0 ? void 0 : _e$dataTransfer.length;
    if (fileLength > 1 && !allowMultipleFiles) {
      setUploadState('error');
      onMultipleFilesError && onMultipleFilesError();
    } else if ((uploadState === 'init' || uploadState === 'error') && checkFile(e.dataTransfer.files[0])) {
      handleFileChange(e.dataTransfer.files[0], 'drag');
    }
    setActive(false);
  };
  var onDragOver = function onDragOver(e) {
    e.preventDefault();
    if (uploadState === 'init' || uploadState === 'error') setActive(true);
  };
  var removeFile = function removeFile() {
    if (inputRef.current) inputRef.current.value = '';
    onChange === null || onChange === void 0 || onChange(null);
  };
  return /*#__PURE__*/React.createElement(UploadContainer, null, /*#__PURE__*/React.createElement("div", {
    className: classnames(ROOT_PREFIX, (_classnames = {}, _defineProperty(_classnames, "".concat(ROOT_PREFIX, "-active"), active), _defineProperty(_classnames, "".concat(ROOT_PREFIX, "-customDisplay"), successDisplay && uploadState === 'success' || initDisplay && uploadState === 'init'), _classnames)),
    onDrop: onDrop,
    onDragOver: onDragOver,
    onDragLeave: function onDragLeave() {
      setActive(false);
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: classnames("".concat(ROOT_PREFIX, "-content"), "".concat(ROOT_PREFIX, "-content-").concat(uploadState))
  }, /*#__PURE__*/React.createElement("input", {
    accept: accept,
    ref: inputRef,
    onChange: _onChange,
    type: "file"
  }), uploadState === 'init' && (initDisplay !== null && initDisplay !== void 0 ? initDisplay : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("i", {
    className: "icon-import-cloud-fill"
  }), /*#__PURE__*/React.createElement("div", null, uploadDescription ? uploadDescription : intl.formatMessage({
    id: 'embers.upload.browseOrDrag',
    defaultMessage: '{browse} or drag file here'
  }, {
    browse: /*#__PURE__*/React.createElement(EmbersLink, {
      onClick: onClick,
      component: "button"
    }, intl.formatMessage({
      id: 'embers.upload.browse',
      defaultMessage: 'Browse'
    }))
  })))), uploadState === 'processing' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(EmbersLoading, {
    size: "medium"
  }), /*#__PURE__*/React.createElement("div", null, intl.formatMessage({
    id: 'embers.upload.processing',
    defaultMessage: 'processing ...'
  }))), uploadState === 'success' && (successDisplay !== null && successDisplay !== void 0 ? successDisplay : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(EmbersStatusIcon, {
    className: "upload-status-icon",
    type: "success",
    variant: "outline",
    size: "large"
  }), /*#__PURE__*/React.createElement("div", {
    className: "success-area"
  }, /*#__PURE__*/React.createElement("div", null, fileName), /*#__PURE__*/React.createElement(InteractiveIcon, {
    iconName: "icon-delete",
    onClick: removeFile,
    label: deleteLabel !== null && deleteLabel !== void 0 ? deleteLabel : intl.formatMessage({
      id: 'embers.upload.eA.delete',
      defaultMessage: 'remove file'
    })
  })))), uploadState === 'error' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(EmbersStatusIcon, {
    type: "error",
    variant: "filled",
    iconName: "icon-import-cloud-error-fill",
    size: "large",
    className: "upload-status-icon",
    "aria-label": intl.formatMessage({
      id: 'embers.statusIcon.error',
      defaultMessage: 'error'
    })
  }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: "error-title"
  }, errorMessage === null || errorMessage === void 0 ? void 0 : errorMessage.title), /*#__PURE__*/React.createElement("div", {
    className: "error-content"
  }, errorMessage === null || errorMessage === void 0 ? void 0 : errorMessage.content))))));
};
export var EmbersUpload = /*#__PURE__*/forwardRef(Upload);